import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {ConfigService} from '@ngmedax/config';
import {RegistryService} from '@ngmedax/registry';
import {configKeys} from '../patient.config-keys';
import {Patient, Survey, SurveyCode} from '../../../types';


@Injectable()
export class SurveyService {
  /**
   * Injects dependencies
   */
  public constructor(
    private registryService: RegistryService,
    private configService: ConfigService,
    private http: HttpClient
  ) {
  }

  /**
   * Creates a survey for given patient and locale
   */
  public createSurvey(patient: Patient, locale = 'de_DE'): Promise<Survey> {
    const basePath = `${this.getPatientApiUrl()}/${patient.uid}/survey`;

    const allowMail = patient.allowMail;
    const prefill = patient.prefill;
    const questionnaires = patient.questionnaires.map(q => q.id);
    const body = {locale, allowMail, prefill, questionnaires};

    return new Promise((resolve, reject) => {
      this.http.post(basePath, body, {headers: this.getAuthHeaders()})
        .subscribe((result: any) => {
            if (!result || typeof result !== 'object' || !result.success) {
              reject(result.message || 'Unknown error');
              return;
            }
            resolve(result.survey);
          },
          error => {
            reject(error);
          });
    });
  }

  /**
   * Creates a survey code for a survey by given survey id
   */
  public createSurveyCode(surveyId: string): Promise<SurveyCode> {
    const basePath = `${this.getSurveyApiUrl()}/code/${surveyId}`;

    return new Promise((resolve, reject) => {
      this.http.post(basePath, {}, {headers: this.getAuthHeaders()})
        .subscribe((result: any) => {
            if (!result || typeof result !== 'object' || !result.success) {
              reject(result.message || 'Unknown error');
              return;
            }
            resolve(result.surveyCode);
          },
          error => {
            reject(error);
          });
    });
  }

  /**
   * Returns api url for patient
   *
   * @param {string} suffix
   * @returns {string}
   */
  private getPatientApiUrl(suffix: string = null): string {
    return this.buildUrl(configKeys.PATIENT_URI_CONFIG_KEY, suffix);
  }

  /**
   * Returns api url for survey
   *
   * @param {string} suffix
   * @returns {string}
   */
  private getSurveyApiUrl(suffix: string = null): string {
    return this.buildUrl(configKeys.SURVEY_API_URI_CONFIG_KEY, suffix);
  }

  /**
   * Returns url for config key. Adds auth information to url path when api is not deprecated
   *
   * @param {string} configKey
   * @returns {any}
   */
  private buildUrl(configKey: string, suffix = null) {
    let uri = this.configService.get(configKey);

    if (suffix) {
      uri = `${uri}${suffix}`;
    }

    return uri;
  }

  /**
   * Returns auth headers by auth token and tenant id
   */
  private getAuthHeaders(): any {
    const headers: any = {};
    const authToken = this.registryService.get(configKeys.SESSION_AUTH_TOKEN);
    const tenantId = this.registryService.get(configKeys.SESSION_TENANT_ID);

    if (authToken) {
      headers['X-Api-Token'] = authToken;
    }

    if (tenantId) {
      headers['X-Api-TenantId'] = `${tenantId}`;
    }

    return headers;
  }
}
